import Config from 'Config';
import {
    WORK_ORDER_STATUS,
    QUOTE_STATUS,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { Client } from 'common/types/client';
import { DisplayType } from './meta-data';
import { S3StorageInfo } from './s3Storage';

export interface Requirement {
    id: string;
    photoTypeId: string;
    fileName: string;
    description: string;
    url?: string;
    uploadedBy: string;
    mimeType: string;
    requiresPullAndClean?: boolean;
    photo?: boolean;
    exceptionFlagId?: string;
    thumbnail?: S3StorageInfo;
    video?: S3StorageInfo;
}

export interface VideoRequirement {
    id: string;
    photoTypeId: string;
    fileName: string;
    description: string;
    url?: string;
    uploadedBy: string;
    mimeType: string;
    requiresPullAndClean?: boolean;
    exceptionFlagId?: string;
    thumbnail?: S3StorageInfo;
    video?: S3StorageInfo;
}

export interface Photo {
    id: number;
    workOrderId: number;
    requirement: Requirement | VideoRequirement;
    created: string;
    approvalVersion: number;
    photoMetadata: PhotoMetadata;
    videoMetadata: VideoMetadata;
    approvalStatusId?: string;
    approval?: {
        additionalNotes?: string;
        reasons?: string[];
        additionalReasons?: string[];
        reasonOther?: string;
    };
    requirementTypeId: string;
    userId?: number;
    typeId?: string;
    createdById?: string;
    updatedById?: string;
    updated?: string;
    photoPrediction?: {
        classification: {
            classificationScore: number;
            validClassification: boolean;
        };
        identification: {
            validIdentification: boolean;
        };
    };
    photoIntegrity?: PhotoIntegrity;
    action: keyof typeof ACTION | keyof typeof APPROVAL_STATUS;
}

export interface PhotoMetadata {
    metadata: { photoAddress: string; timezoneId: string };
}

export interface VideoMetadata {
    metadata: {
        duration: number;
        width: number;
        height: number;
        format: string;
    };
}

export interface PhotoApproval {
    approvalStatusId: string;
    reasons: (string | ApprovalReason)[];
    additionalReasons: (string | DisplayType)[];
    additionalNotes: string | undefined;
    reasonOther: string | undefined;
    manufacturerYear: '' | number;
    changeReason?: string;
    changeReasonOther?: string;
}

export interface PhotoIntegrity {
    validateDuplication: {
        duplicateList: Array<{
            refDocumentId: string;
            refDocumentTypeId: string;
            refRequirementId: string;
            refPhotoId: string;
            refPhotoUrl: string;
            refPhotoMimeType: string;
            refPhotoCreated: string;
            refPhotoApprovalStatusId: string;
            refPhotoUserId: string;
            refPhotoPrediction?: {
                classification: {
                    classificationScore: number;
                    validClassification: boolean;
                };
                identification: {
                    validIdentification: boolean;
                };
            };
        }>;
        validDuplicate: boolean;
    };
    validateCreationDate: {
        capturedDate: string;
        validCreationDate: boolean;
    };
    validateLocation: {
        validLocation: boolean;
        photoAddress: string;
    };
    validIntegrity: boolean;
}

export interface PhotoTypeRequirement {
    requirement: {
        id: string;
    };
}

export interface VideoTypeRequirement {
    requirement: {
        id: string;
    };
}

export interface QuotePhotoTypeRequirement {
    id: string;
    display: string;
}

export interface ApprovalReason {
    id: string;
    display: string;
    additionalReasons?: DisplayType[];
}

export interface LhsRiskType {
    id: string;
    display: string;
    reasons: Record<string, { display: string }>;
}

export interface IssueTracking {
    id: string;
    active: boolean;
    issueReasonId: string[];
    issueSecondaryReasonId?: string[];
    issueOtherReason?: string;
}

export interface WorkOrderLHS {
    riskType: string;
    riskReasons: string[];
    otherReason?: string;
}

export interface CheckIn {
    checkedOutAt?: string;
    checkedInUserId: number;
}

export interface Document {
    id: number;
    documentTypeId: string;
    statusId: WORK_ORDER_STATUS | QUOTE_STATUS;
    issueTracking?: IssueTracking[];
    workOrderLHS?: WorkOrderLHS[];
    client: Client;
    tradeTypeId: string;
    tradeSubTypeId: string;
    workOrderTypeId: string;
    checkInsV2: CheckIn[];
    property: {
        stdAddress: string;
        timezone: string;
    };
    number: number;
}

export interface AcceptedFile extends File {
    preview: string;
}

export const APPROVAL_STATUS = {
    ACCEPT: {
        color: Config.color.mediumGray,
        display: 'ACCEPTED',
        newUIDisplay: 'Accepted',
        id: 'ACCEPT',
    },
    APPROVE: {
        color: Config.color.green,
        display: 'APPROVED',
        newUIDisplay: 'Approved',
        id: 'APPROVE',
    },
    REJECT: {
        color: Config.color.red,
        display: 'REJECTED',
        newUIDisplay: 'Rejected',
        id: 'REJECT',
    },
};

export const ACTION = {
    APPROVE: { display: 'APPROVED' },
    REJECT: { display: 'REJECTED' },
    ACCEPT: { display: 'ACCEPTED' },
    UPLOAD: { display: 'UPLOADED' },
    'RE-UPLOAD': { display: 'RE-UPLOADED' },
    REMOVE: { display: 'REMOVE' },
    'UPDATE PHOTO TYPE': { display: 'UPDATE PHOTO TYPE' },
};
